import { type FormEvent, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';

import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { AuthApiError } from '@/api/client';
import { errorMessage } from '@/lib/errorMap';
import { useAuthStore } from '@/stores/useAuthStore';

/**
 * Phase 5.5b — login 1단계 후 MFA 코드 입력 화면.
 * Phase 5c.C — method (totp/email/...) 별 안내 문구 분기.
 *
 * 쿼리 파라미터:
 * - cid: challenge_id
 * - method: totp / email / sms_ncp / kakao_alimtalk (default totp)
 * - hint: recipient_hint (이메일 마스킹 등 — UI 표시용)
 * - redirect: 성공 후 이동할 경로
 */
export function MfaChallengePage() {
  const [params] = useSearchParams();
  const challengeId = params.get('cid') ?? '';
  const method = params.get('method') ?? 'totp';
  const recipientHint = params.get('hint') ?? '';
  const redirect = params.get('redirect') ?? '/';
  const [code, setCode] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const loginMfa = useAuthStore((s) => s.loginMfa);
  const navigate = useNavigate();

  const description = (() => {
    switch (method) {
      case 'email':
        return recipientHint
          ? `${recipientHint} 으로 발송된 6자리 코드를 입력하세요.`
          : '등록된 이메일로 발송된 6자리 코드를 입력하세요.';
      case 'sms_ncp':
        return recipientHint
          ? `${recipientHint} 으로 발송된 6자리 코드를 입력하세요.`
          : '등록된 휴대폰으로 발송된 6자리 코드를 입력하세요.';
      case 'kakao_alimtalk':
        return '카카오 알림톡으로 발송된 6자리 코드를 입력하세요.';
      case 'totp':
      default:
        return 'Authenticator 앱의 6자리 코드 또는 backup 코드를 입력하세요.';
    }
  })();

  const onSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!challengeId) {
      toast.error('challenge_id 가 없습니다. 다시 로그인하세요.');
      navigate('/login', { replace: true });
      return;
    }
    setSubmitting(true);
    try {
      await loginMfa(challengeId, code.trim());
      toast.success('로그인 됐습니다');
      navigate(redirect, { replace: true });
    } catch (e) {
      if (e instanceof AuthApiError) {
        if (e.code === 'invalid_challenge') {
          toast.error('인증 시간이 만료됐습니다. 다시 로그인하세요.');
          navigate('/login', { replace: true });
          return;
        }
        toast.error(errorMessage(e.code));
      } else {
        toast.error('네트워크 오류');
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-background p-4">
      <Card className="w-full max-w-sm">
        <CardHeader>
          <CardTitle>2단계 인증</CardTitle>
          <CardDescription>{description}</CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={onSubmit} className="flex flex-col gap-4">
            <div className="flex flex-col gap-2">
              <Label htmlFor="code">인증 코드</Label>
              <Input
                id="code"
                value={code}
                onChange={(e) => setCode(e.target.value)}
                placeholder="123456"
                autoComplete="one-time-code"
                inputMode="text"
                autoFocus
                required
              />
            </div>
            <Button type="submit" disabled={submitting || code.length < 6} className="mt-2">
              {submitting ? '인증 중...' : '확인'}
            </Button>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={() => navigate('/login', { replace: true })}
            >
              취소 (다시 로그인)
            </Button>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}
